import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  selectSessionAdvisor,
  fetchSessionForAuthRequest,
  selectFeatureFlags,
} from '@client/model';
import { advisorsApi, advisorOnboardingKeys } from '@client/api/waterlily';
import {
  getLockedAdvisorOrgNames,
  isQuestionnaireRequiredForAdvisor,
  ORG_TYPE_OPTIONS,
  type OrgType,
} from '@shared';

const ORG_TYPE_VALUES = ORG_TYPE_OPTIONS.map(o => o.value) as [OrgType, ...OrgType[]];

const EDJ_LOCKED_ORG_TYPE: OrgType = 'broker-dealer';

export const advisorBasicInfoSchema = z.object({
  advisorFirstName: z.string().min(1, 'First name is required'),
  advisorLastName: z.string().min(1, 'Last name is required'),
  organizationDisplayName: z.string().min(1, 'Organization name is required'),
  organizationName: z.string().optional(),
  orgType: z.enum(ORG_TYPE_VALUES).optional(),
});

export type AdvisorBasicInfoFormValues = z.infer<typeof advisorBasicInfoSchema>;

interface UseAdvisorOnboardingBasicInfoFormOptions {
  onSaveSuccess?: () => void;
  /**
   * The advisor's currently-persisted orgType. Lives in AdvisorOrganizations, so it has
   * to be sourced by the caller (e.g. from the questionnaire payload) rather than read
   * off the session advisor record.
   */
  currentOrgType?: OrgType | null;
}

export function useAdvisorOnboardingBasicInfoForm({
  onSaveSuccess,
  currentOrgType,
}: UseAdvisorOnboardingBasicInfoFormOptions = {}) {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const advisor = useSelector(selectSessionAdvisor);
  const featureFlags = useSelector(selectFeatureFlags);
  const isEdjPilotAdvisor = !!featureFlags.edjPilotAdvisor;
  const lockedOrg = getLockedAdvisorOrgNames(featureFlags);
  const isOrgLocked = !!lockedOrg;
  // orgType is only required for advisors who must complete the questionnaire. Advisors who
  // onboarded before it are grandfathered out, so we neither show nor require the field.
  const showOrgType = isQuestionnaireRequiredForAdvisor(advisor);

  const form = useForm<AdvisorBasicInfoFormValues>({
    resolver: zodResolver(advisorBasicInfoSchema),
    mode: 'onChange',
    defaultValues: {
      advisorFirstName: '',
      advisorLastName: '',
      organizationDisplayName: '',
      organizationName: '',
      orgType: undefined,
    },
  });

  const updateAdvisorMutation = useMutation({
    mutationFn: (data: AdvisorProfile) => advisorsApi.updateById(advisor.advisorId!, data),
    onSuccess: async () => {
      toast.success('Profile Saved', {
        description: 'Your information has been saved.',
      });
      dispatch(fetchSessionForAuthRequest({ invalidating: true }));
      if (advisor.advisorId) {
        await queryClient.invalidateQueries({
          queryKey: advisorOnboardingKeys.questionnaire(advisor.advisorId),
        });
      }
      onSaveSuccess?.();
    },
    onError: () => {
      toast.error('Failed to save profile', {
        description: 'An unexpected error occurred. Please try again.',
      });
    },
  });

  // Check if the advisor profile has all required fields filled (from the persisted advisor data)
  const isProfileComplete = useMemo(() => {
    const basicsFilled = Boolean(
      advisor.advisorFirstName?.trim() &&
      advisor.advisorLastName?.trim() &&
      advisor.organizationDisplayName?.trim(),
    );

    if (!showOrgType) return basicsFilled;

    return basicsFilled && !!currentOrgType;
  }, [
    advisor.advisorFirstName,
    advisor.advisorLastName,
    advisor.organizationDisplayName,
    currentOrgType,
    showOrgType,
  ]);

  const orgTypeValue = form.watch('orgType');
  const isFormValid =
    form.formState.isValid && (!showOrgType || isEdjPilotAdvisor || !!orgTypeValue);

  const [isFormInitialized, setIsFormInitialized] = useState(false);

  useEffect(() => {
    if (advisor.advisorId && (!isFormInitialized || !form.formState.isDirty)) {
      form.reset({
        advisorFirstName: advisor.advisorFirstName ?? '',
        advisorLastName: advisor.advisorLastName ?? '',
        organizationDisplayName:
          lockedOrg?.organizationDisplayName ?? advisor.organizationDisplayName ?? '',
        organizationName: lockedOrg?.organizationName ?? advisor.organizationName ?? '',
        orgType: showOrgType
          ? isEdjPilotAdvisor
            ? EDJ_LOCKED_ORG_TYPE
            : ((currentOrgType as OrgType | undefined) ?? undefined)
          : undefined,
      });
      setIsFormInitialized(true);
    }
  }, [
    advisor.advisorId,
    advisor.advisorFirstName,
    advisor.advisorLastName,
    advisor.organizationDisplayName,
    advisor.organizationName,
    currentOrgType,
    isEdjPilotAdvisor,
    lockedOrg?.organizationDisplayName,
    lockedOrg?.organizationName,
    isFormInitialized,
    showOrgType,
    form,
  ]);

  const onSubmit = (data: AdvisorBasicInfoFormValues) => {
    if (!advisor.advisorId) {
      toast.error('Unable to proceed', {
        description: 'Advisor ID is missing. Please refresh and try again.',
      });
      return;
    }

    if (!form.formState.isDirty && isProfileComplete) {
      onSaveSuccess?.();
      return;
    }

    const displayName = lockedOrg?.organizationDisplayName ?? data.organizationDisplayName.trim();
    const legalName = lockedOrg?.organizationName ?? (data.organizationName?.trim() || displayName);

    const orgTypePayload: { orgType?: OrgType } = {};

    if (showOrgType) {
      orgTypePayload.orgType = isEdjPilotAdvisor ? EDJ_LOCKED_ORG_TYPE : data.orgType;
    }

    updateAdvisorMutation.mutate(
      {
        ...advisor,
        advisorFirstName: data.advisorFirstName.trim(),
        advisorLastName: data.advisorLastName.trim(),
        organizationName: legalName,
        organizationDisplayName: displayName,
        ...orgTypePayload,
      },
      {
        onSuccess: () => {
          // Reset the form with the saved values to clear the dirty state
          form.reset({
            advisorFirstName: data.advisorFirstName.trim(),
            advisorLastName: data.advisorLastName.trim(),
            organizationDisplayName: displayName,
            organizationName: legalName,
            orgType: orgTypePayload.orgType,
          });
        },
      },
    );
  };

  return {
    form,
    onSubmit: form.handleSubmit(onSubmit),
    isSubmitting: updateAdvisorMutation.isPending,
    isValid: isFormValid,
    isProfileComplete,
    isFormInitialized,
    isOrgLocked,
    showOrgType,
  };
}
